import { GetStaticPaths, NextPage } from 'next'
import { coreActions } from '@ucheba/store/core'
import { IPageProps } from '@ucheba/utils/helpers/components/types'
import { useDispatch, useSelector } from 'react-redux'
import { institutionsProgramIdSelectors } from '@ucheba/store/institutions/programs/programId'
import { authActions, authSelectors } from '@ucheba/store/auth'
import React, { useEffect, useMemo } from 'react'
import { setParams } from '@ucheba/utils/helpers/analytics/ga'
import { plannerProgramsThunks } from '@ucheba/store/planner/programs'
import { learningRequestThunk } from '@ucheba/store/institutions/learningRequest'
import { probabilitiesThunks } from '@ucheba/store/probabilities'
import { feedbacksThunks } from '@ucheba/store/feedbacks'
import { questionsThunks } from '@ucheba/store/questions'
import { EActivityTypes } from '@ucheba/utils/types/userActivityTypes'
import { NextSeo } from 'next-seo'
import { EProgramTypes } from '@ucheba/utils/types/program'
import Custom404 from '../../../404.page'
import { getProgramDescription } from '../../ssr/[id]/index/bll/getProgramDescription'
import { useGetInstitutionsId } from '../../ssr/[id]/index/bll/useGetInstitutionsId'
import { useUserActivity } from '../../../../../utils/hooks/useUserActivity'
import { getIndexProgramPageProps } from '../../bll/getIndexProgramPageProps'
import { wrapper } from '../../../../store/store'
import Page from '../../ssr/[id]/index/desktop'
import { revalidate } from '../../constants'
import { useProgramCanonical } from '../../utils/useProgramCanonical'

const IndexProgramPage: NextPage<IPageProps & { redirected: boolean }> = (props) => {
  const program = useSelector(institutionsProgramIdSelectors.entity)
  const { institution } = program || {}
  const { redirected } = props
  const dispatch = useDispatch()
  const isAuth = useSelector(authSelectors.isAuth)
  const { canonical } = useProgramCanonical()

  const params = useMemo(() => {
    const obj: any = {}

    if (program) {
      if (program.institution?.id) {
        obj.dimension1 = program.institution?.id
      }

      if (program.faculty?.id) {
        obj.dimension2 = program.faculty?.id
      }

      obj.dimension3 = program.id
    }

    return obj
  }, [program])

  setParams(params)

  const { setUserActivity } = useUserActivity()
  const institutionId = useGetInstitutionsId()

  useEffect(() => {
    if (isAuth && program) {
      dispatch<any>(plannerProgramsThunks.getProgramIds({}))

      dispatch<any>(learningRequestThunk.fetchLearningRequestsIds({}))

      if (
        program?.programType?.value === EProgramTypes.higherBachelor ||
        program?.programType?.value === EProgramTypes.higherSpeciality
      ) {
        dispatch<any>(
          probabilitiesThunks.check({
            data: {
              type: 'program',
              referId: program.id,
            },
          })
        )
      }

      dispatch<any>(feedbacksThunks.fetch({ data: { id: program.id, type: 'program' } }))

      dispatch<any>(questionsThunks.fetch({ data: { id: program.id, type: 'program' } }))
    }
  }, [isAuth, program?.id])

  useEffect(() => {
    const { href } = window.location

    if (program && institutionId) {
      setUserActivity({
        activityType: EActivityTypes.viewing_institution_info,
        institutionId,
        data: { url: href },
        programId: program.id,
      })
    }
  }, [])

  if (redirected) {
    return null
  }

  if (!program) {
    return <Custom404 />
  }

  const title = `${program.name}, ${program?.programType?.name || ''}, ${
    program?.speciality?.codeOkso || ''
  } - ${institution?.abbreviation}`

  const description = getProgramDescription(program)

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        openGraph={{
          description,
          title,
        }}
      />

      <Page {...(props as any)} />
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = wrapper.getStaticProps(
  (store) =>
    async (ctx): Promise<any> => {
      const { dispatch } = store

      const { notFound } = await getIndexProgramPageProps({ ctx, store })

      if (notFound) {
        return {
          notFound: true,
          revalidate,
        }
      }

      const isAuth = authSelectors.isAuth(store.getState())

      if (isAuth) {
        dispatch(authActions.logout())
      }

      dispatch(coreActions.toggleTouch(false))

      return {
        props: {},
        revalidate,
      }
    }
)

export default IndexProgramPage
